import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi, salesApi, purchaseApi, adminApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

// ── Voucher type definitions ───────────────────────────────────────────────────
type VoucherType = 'CRV' | 'CPV' | 'BRV' | 'BPV' | 'JV';
const VOUCHER_TYPES: Array<{
  code: VoucherType; label: string; category: 'Cash' | 'Bank' | 'Journal';
  direction: 'receipt' | 'payment' | 'journal';
  description: string; hint: string;
  defaultMethod: string;
}> = [
  { code: 'CRV', label: 'CRV', category: 'Cash', direction: 'receipt',
    description: 'Cash Receipt', hint: 'Dr Cash → Cr AR / Revenue',
    defaultMethod: 'cash' },
  { code: 'CPV', label: 'CPV', category: 'Cash', direction: 'payment',
    description: 'Cash Payment', hint: 'Dr Expense/AP → Cr Cash',
    defaultMethod: 'cash' },
  { code: 'BRV', label: 'BRV', category: 'Bank', direction: 'receipt',
    description: 'Bank Receipt', hint: 'Dr Bank → Cr AR / Revenue',
    defaultMethod: 'wire' },
  { code: 'BPV', label: 'BPV', category: 'Bank', direction: 'payment',
    description: 'Bank Payment', hint: 'Dr Expense/AP → Cr Bank',
    defaultMethod: 'wire' },
  { code: 'JV',  label: 'JV',  category: 'Journal', direction: 'journal',
    description: 'Journal Entry', hint: 'Free-form double-entry adjustment',
    defaultMethod: '' },
];

// ── Payment methods ────────────────────────────────────────────────────────────
const CASH_METHODS  = ['cash', 'check'] as const;
const BANK_METHODS  = ['wire', 'ach', 'online_transfer', 'visa', 'mastercard', 'jazzcash', 'easypaisa', 'raast', 'other'] as const;
const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash', check: 'Cheque/Check', wire: 'Bank Wire / NEFT',
  ach: 'ACH / Direct Debit', online_transfer: 'Online Transfer',
  visa: 'Visa Card', mastercard: 'Mastercard',
  jazzcash: 'JazzCash', easypaisa: 'Easypaisa', raast: 'Raast',
  other: 'Other',
};
const ONLINE_METHODS = new Set(['visa','mastercard','online_transfer','jazzcash','easypaisa','raast']);
const GATEWAY_OPTIONS = ['manual','stripe','payfast','nift','raast','jazzcash','easypaisa','2checkout'];

// ── Voucher number (reserve-on-open, show, release-on-close) ──────────────────
function VoucherNumber({ docType }: { docType: string }) {
  const [number, setNumber] = useState<string>('…');
  const reservationId = useRef<string | null>(null);
  useEffect(() => {
    let active = true;
    adminApi.reserveNumber(docType)
      .then((r: any) => {
        if (!active) { if (r?.reservationId) adminApi.releaseNumber(r.reservationId).catch(() => {}); return; }
        reservationId.current = r.reservationId;
        setNumber(r.number);
      })
      .catch(() => { if (active) setNumber('(auto)'); });
    return () => {
      active = false;
      if (reservationId.current) adminApi.releaseNumber(reservationId.current).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);
  return (
    <div>
      <label className="label">Voucher #</label>
      <div className="input flex items-center font-mono font-semibold text-ink-900 bg-canvas-subtle">{number}</div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export function VouchersPage() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [defaultType, setDefaultType] = useState<VoucherType>('CRV');

  // Recent vouchers = journal entries with voucher_type set + recent payments
  const recentJE = useQuery({
    queryKey: ['je', 'vouchers'],
    queryFn: () => accountingApi.listJournalEntries({ limit: 50 }),
  });
  const recentArPay = useQuery({
    queryKey: ['ar', 'payments', 'recent'],
    queryFn: () => accountingApi.listCustomerPayments({ limit: 30 }),
  });
  const recentApPay = useQuery({
    queryKey: ['ap', 'payments', 'recent'],
    queryFn: () => accountingApi.listVendorPayments({ limit: 30 }),
  });

  const voucherActionMut = useMutation({
    mutationFn: ({ action, id }: { action: string; id: string }) => {
      if (action === 'delete-je') return accountingApi.deleteJournalEntry(id);
      if (action === 'reverse-je') return accountingApi.reverseJournalEntry(id, { reason: 'Reversed from Vouchers' });
      if (action === 'reverse-ar') return accountingApi.reverseArPayment(id);
      return accountingApi.reverseApPayment(id);
    },
    onSuccess: (r: any) => {
      toast.success(r?.message || 'Voucher updated');
      qc.invalidateQueries({ queryKey: ['je'] });
      qc.invalidateQueries({ queryKey: ['ar'] });
      qc.invalidateQueries({ queryKey: ['ap'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const recentRows = useMemo(() => {
    const ar = (recentArPay.data ?? []).map((p: any) => ({
      id: p.id, date: p.payment_date,
      type: p.voucher_type ?? (p.method === 'cash' ? 'CRV' : 'BRV'),
      number: p.payment_number, party: p.customer_name, amount: p.amount,
      method: p.method, status: 'posted', side: 'ar',
    }));
    const ap = (recentApPay.data ?? []).map((p: any) => ({
      id: p.id, date: p.payment_date,
      type: p.voucher_type ?? (p.method === 'cash' ? 'CPV' : 'BPV'),
      number: p.payment_number, party: p.supplier_name, amount: p.amount,
      method: p.method, status: 'posted', side: 'ap',
    }));
    const je = (recentJE.data ?? [])
      .filter((e: any) => e.voucher_type === 'JV' || e.source_type === 'manual')
      .map((e: any) => ({
        id: e.id, date: e.entry_date,
        type: e.voucher_type ?? 'JV',
        number: e.entry_number, party: e.description ?? '—',
        amount: parseFloat(e.total_debit), method: '—', status: e.status, side: 'je',
      }));
    return [...ar, ...ap, ...je]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 50);
  }, [recentArPay.data, recentApPay.data, recentJE.data]);

  const kpis = useMemo(() => {
    const counts: Record<VoucherType, number> = { CRV: 0, CPV: 0, BRV: 0, BPV: 0, JV: 0 };
    for (const r of recentRows) if (r.type in counts) counts[r.type as VoucherType]++;
    return counts;
  }, [recentRows]);

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader eyebrow="Accounting · Finance" title="Vouchers"
        subtitle="CRV · CPV · BRV · BPV · JV — record cash, bank, and journal transactions from one place."
        actions={
          <div className="flex gap-2">
            {(['CRV','CPV','BRV','BPV','JV'] as VoucherType[]).map((t) => (
              <button key={t} className="btn-secondary text-xs font-mono font-bold !px-3 !py-2"
                onClick={() => { setDefaultType(t); setShowModal(true); }}>
                + {t}
              </button>
            ))}
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {VOUCHER_TYPES.map((vt) => {
          const tones: Record<string, string> = {
            CRV: 'bg-sage-500', CPV: 'bg-danger', BRV: 'bg-finance-500', BPV: 'bg-amber-500', JV: 'bg-ink-400',
          };
          return (
            <div key={vt.code} className="card p-4 relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => { setDefaultType(vt.code); setShowModal(true); }}>
              <div className={`absolute top-0 left-0 bottom-0 w-1 ${tones[vt.code]} rounded-l-xl`} />
              <div className="pl-3">
                <div className="font-mono font-bold text-sm text-ink-900">{vt.code}</div>
                <div className="text-2xs text-ink-500">{vt.description}</div>
                <div className="mt-2 text-2xl font-bold font-display text-ink-900 tabular-nums">{kpis[vt.code]}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent vouchers table */}
      {recentArPay.isLoading || recentApPay.isLoading ? (
        <div className="card"><LoadingState rows={6} /></div>
      ) : (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between">
            <div className="font-semibold text-ink-900">Recent vouchers</div>
            <div className="text-xs text-ink-500">{recentRows.length} records</div>
          </div>
          {recentRows.length === 0 ? (
            <EmptyState message="No vouchers yet"
              hint="Click any voucher type button above to create your first entry."
              action={<button className="btn-primary" onClick={() => setShowModal(true)}>+ New voucher</button>}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th><th>Number</th><th>Date</th>
                    <th>Party</th><th>Method</th>
                    <th className="text-right">Amount</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentRows.map((r) => (
                    <tr key={`${r.side}-${r.id}`}>
                      <td><VoucherBadge type={r.type as VoucherType} /></td>
                      <td className="font-mono text-xs font-semibold text-ink-900">{r.number}</td>
                      <td className="text-xs text-ink-600 tabular-nums">
                        {new Date(r.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="text-sm text-ink-700 truncate max-w-[200px]">{r.party || '—'}</td>
                      <td className="text-xs text-ink-600 capitalize">{METHOD_LABELS[r.method] ?? r.method}</td>
                      <td className="text-right font-mono font-semibold tabular-nums text-ink-900">
                        {parseFloat(String(r.amount)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        <span className={clsx('badge ring-1 ring-inset text-2xs capitalize',
                          r.status === 'posted' ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-canvas-subtle text-ink-600 ring-ink-200')}>
                          {r.status}
                        </span>
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {r.status === 'reversed' ? (
                          <span className="text-2xs text-ink-400">reversed</span>
                        ) : r.side === 'je' && (r.status === 'draft' || r.status === 'under_review' || r.status === 'approved') ? (
                          <button className="text-xs font-medium px-2 py-1 rounded text-red-600 hover:bg-red-50"
                            disabled={voucherActionMut.isPending}
                            onClick={() => { if (window.confirm(`Delete draft voucher ${r.number}?`)) voucherActionMut.mutate({ action: 'delete-je', id: r.id }); }}>
                            Delete
                          </button>
                        ) : (
                          <button className="text-xs font-medium px-2 py-1 rounded text-amber-700 hover:bg-amber-50"
                            disabled={voucherActionMut.isPending}
                            onClick={() => { if (window.confirm(`Reverse voucher ${r.number}? This creates an offsetting entry and restores any affected balances.`)) voucherActionMut.mutate({ action: r.side === 'je' ? 'reverse-je' : r.side === 'ar' ? 'reverse-ar' : 'reverse-ap', id: r.id }); }}>
                            Reverse
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <VoucherModal
          defaultType={defaultType}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            qc.invalidateQueries({ queryKey: ['ar'] });
            qc.invalidateQueries({ queryKey: ['ap'] });
            qc.invalidateQueries({ queryKey: ['je'] });
          }}
        />
      )}
    </div>
  );
}

// ── VoucherBadge ───────────────────────────────────────────────────────────────
function VoucherBadge({ type }: { type: VoucherType }) {
  const TONE: Record<VoucherType, string> = {
    CRV: 'bg-sage-50 text-sage-700 ring-sage-200',
    CPV: 'bg-red-50 text-red-700 ring-red-200',
    BRV: 'bg-blue-50 text-blue-700 ring-blue-200',
    BPV: 'bg-amber-50 text-amber-700 ring-amber-200',
    JV:  'bg-canvas-subtle text-ink-700 ring-ink-200',
  };
  return <span className={clsx('badge ring-1 ring-inset font-mono font-bold text-2xs', TONE[type])}>{type}</span>;
}

// ── VoucherModal ───────────────────────────────────────────────────────────────
function VoucherModal({ defaultType, onClose, onSuccess }: {
  defaultType: VoucherType; onClose: () => void; onSuccess: () => void;
}) {
  const [type, setType] = useState<VoucherType>(defaultType);
  const vt = VOUCHER_TYPES.find((v) => v.code === type)!;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden">

      {/* Header */}
      <div className="px-6 pt-5 pb-3 border-b border-ink-100 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow text-ink-500">Accounting · Vouchers</div>
          <button className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-ink-900 rounded transition-colors"
            onClick={onClose} aria-label="Close">✕</button>
        </div>
        <h2 className="text-xl font-bold font-display text-ink-900">New Voucher</h2>

        {/* Type selector */}
        <div className="flex gap-1.5 mt-3">
          {VOUCHER_TYPES.map((v) => (
            <button key={v.code}
              onClick={() => setType(v.code)}
              className={clsx(
                'flex-1 flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl text-center border transition-all',
                type === v.code
                  ? 'bg-finance-600 border-finance-600 text-white shadow-md'
                  : 'bg-canvas-subtle border-ink-200 text-ink-600 hover:border-ink-400'
              )}>
              <span className="text-xs font-mono font-bold">{v.code}</span>
              <span className={clsx('text-2xs', type === v.code ? 'text-finance-100' : 'text-ink-400')}>{v.category}</span>
            </button>
          ))}
        </div>

        {/* Hint */}
        <div className="mt-2 px-3 py-2 bg-finance-50 border border-finance-200 rounded-lg text-xs text-finance-700 font-medium">
          {vt.hint}
        </div>
      </div>

      {/* Body — renders correct form based on type */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {(type === 'CRV' || type === 'BRV') && (
          <ReceiptForm voucherType={type} onClose={onClose} onSuccess={onSuccess} />
        )}
        {(type === 'CPV' || type === 'BPV') && (
          <PaymentForm voucherType={type} onClose={onClose} onSuccess={onSuccess} />
        )}
        {type === 'JV' && (
          <JournalForm onClose={onClose} onSuccess={onSuccess} />
        )}
      </div>
    </Modal>
  );
}

// ── ReceiptForm (CRV + BRV) ────────────────────────────────────────────────────
function ReceiptForm({ voucherType, onClose, onSuccess }: {
  voucherType: 'CRV' | 'BRV'; onClose: () => void; onSuccess: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const methods = voucherType === 'CRV' ? CASH_METHODS : BANK_METHODS;

  const [date, setDate] = useState(today);
  const [reference, setReference] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerQ, setCustomerQ] = useState('');
  const [custOpen, setCustOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [method, setMethod] = useState<string>(voucherType === 'CRV' ? 'cash' : 'wire');
  const [bankAccountCode, setBankAccountCode] = useState('');
  const [gateway, setGateway] = useState('manual');
  const [gatewayTxnId, setGatewayTxnId] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [cardBrand, setCardBrand] = useState('visa');
  const [selectedInvs, setSelectedInvs] = useState<Record<string, number>>({});

  const isOnline = ONLINE_METHODS.has(method);
  const totalAmount = Object.values(selectedInvs).reduce((s, v) => s + v, 0);

  const custSearch = useQuery({
    queryKey: ['cust-search', customerQ],
    queryFn: () => salesApi.listCustomers({ q: customerQ || undefined, limit: 20 }),
    enabled: custOpen || customerQ.length >= 1,
  });
  const openInvs = useQuery({
    queryKey: ['open-invs', customerId],
    queryFn: () => accountingApi.listInvoices({ customerId, status: 'open', limit: 50 }),
    enabled: !!customerId,
  });
  const bankAccts = useQuery({
    queryKey: ['bank-accts'],
    queryFn: () => accountingApi.listAccounts(),
  });
  const bankAccountList = (bankAccts.data ?? []).filter((a: any) => a.is_bank_account);

  const mut = useMutation({
    mutationFn: () => accountingApi.recordCustomerPayment({
      customerId, paymentDate: date, method,
      amount: totalAmount,
      reference: reference || undefined,
      bankAccountCode: bankAccountCode || undefined,
      gateway: isOnline ? gateway : undefined,
      gatewayTxnId: isOnline && gatewayTxnId ? gatewayTxnId : undefined,
      cardLast4: ['visa','mastercard'].includes(method) && cardLast4 ? cardLast4 : undefined,
      cardBrand: ['visa','mastercard'].includes(method) ? cardBrand : undefined,
      voucherType,
      notes: description || undefined,
      applications: Object.entries(selectedInvs)
        .filter(([, amt]) => amt > 0)
        .map(([invoiceId, amount]) => ({ invoiceId, amount })),
    }),
    onSuccess: () => { toast.success(`${voucherType} posted successfully`); onSuccess(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = customerId && totalAmount > 0;

  return (
    <div className="px-6 py-5 space-y-5">
      {/* Date + Reference */}
      <div className="grid grid-cols-3 gap-4">
        <VoucherNumber docType="customer_payment" />
        <div>
          <label className="label">Voucher date</label>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Reference no <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)}
            placeholder="Receipt / transaction reference" />
        </div>
      </div>

      {/* Customer picker */}
      <div>
        <label className="label">Customer <span className="text-danger">*</span></label>
        {customerId ? (
          <div className="flex items-center gap-2 p-3 bg-sage-50 border border-sage-200 rounded-xl">
            <span className="flex-1 text-sm font-semibold text-sage-800">{customerQ || 'Selected'}</span>
            <button className="text-xs text-sage-600 hover:text-sage-800"
              onClick={() => { setCustomerId(''); setCustomerQ(''); setSelectedInvs({}); }}>Change ✕</button>
          </div>
        ) : (
          <div className="relative">
            <input className="input" placeholder="Search or pick a customer…" value={customerQ}
              onChange={(e) => setCustomerQ(e.target.value)}
              onFocus={() => setCustOpen(true)}
              onBlur={() => setTimeout(() => setCustOpen(false), 150)} />
            {custOpen && (custSearch.data?.length ?? 0) > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 card shadow-lg overflow-hidden">
                {custSearch.data!.map((c: any) => (
                  <button key={c.id} className="w-full text-left px-4 py-2.5 hover:bg-canvas-subtle border-b border-ink-100 last:border-0 text-sm"
                    onClick={() => { setCustomerId(c.id); setCustomerQ(c.name); setCustOpen(false); }}>
                    <span className="font-semibold text-ink-900">{c.name}</span>
                    <span className="text-ink-400 ml-2 font-mono text-xs">{c.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Method */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Payment method</label>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value)}>
            {methods.map((m) => (
              <option key={m} value={m}>{METHOD_LABELS[m]}</option>
            ))}
          </select>
        </div>
        {voucherType === 'BRV' && (
          <div>
            <label className="label">Bank account</label>
            <select className="input" value={bankAccountCode} onChange={(e) => setBankAccountCode(e.target.value)}>
              <option value="">— Select bank account —</option>
              {bankAccountList.map((a: any) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Online/card extra fields */}
      {isOnline && (
        <div className="p-4 bg-canvas-subtle rounded-xl border border-ink-200 space-y-3">
          <div className="eyebrow text-ink-500">Online / card details</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Payment gateway</label>
              <select className="input" value={gateway} onChange={(e) => setGateway(e.target.value)}>
                {GATEWAY_OPTIONS.map((g) => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Gateway txn ID</label>
              <input className="input font-mono text-xs" value={gatewayTxnId} onChange={(e) => setGatewayTxnId(e.target.value)}
                placeholder="TXN-2024-XXXX" />
            </div>
            {['visa','mastercard'].includes(method) && (
              <>
                <div>
                  <label className="label">Card brand</label>
                  <select className="input" value={cardBrand} onChange={(e) => setCardBrand(e.target.value)}>
                    <option value="visa">Visa</option>
                    <option value="mastercard">Mastercard</option>
                    <option value="amex">Amex</option>
                    <option value="unionpay">UnionPay</option>
                  </select>
                </div>
                <div>
                  <label className="label">Card last 4 digits</label>
                  <input className="input font-mono tracking-widest text-center" maxLength={4} value={cardLast4}
                    onChange={(e) => setCardLast4(e.target.value.replace(/\D/g,''))} placeholder="••••" />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="label">Description / narration</label>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder={`e.g. Cash received from ${customerQ || 'customer'} for invoice INV-XXXX`} />
      </div>

      {/* Open invoices */}
      {customerId && (
        <div>
          <div className="eyebrow text-ink-500 mb-2">Apply to invoices <span className="font-normal normal-case tracking-normal text-ink-400">(select and enter amounts)</span></div>
          {openInvs.isLoading ? <LoadingState rows={2} />
            : !openInvs.data?.length ? (
              <div className="p-4 text-center text-sm text-ink-400 bg-canvas-subtle rounded-xl border border-ink-200">
                No open invoices for this customer.
              </div>
            ) : (
              <div className="border border-ink-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-canvas-subtle border-b border-ink-200">
                      <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500 w-8"></th>
                      <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Invoice</th>
                      <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Date</th>
                      <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Balance due</th>
                      <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Apply amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(openInvs.data ?? []).map((inv: any) => {
                      const balance = parseFloat(inv.balance_due);
                      const checked = inv.id in selectedInvs;
                      return (
                        <tr key={inv.id} className={clsx('border-b border-ink-100 last:border-0',
                          checked ? 'bg-sage-50/40' : 'hover:bg-canvas-subtle/50')}>
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedInvs((p) => ({ ...p, [inv.id]: balance }));
                                else setSelectedInvs((p) => { const n = { ...p }; delete n[inv.id]; return n; });
                              }} />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs font-bold text-ink-900">{inv.invoice_number}</td>
                          <td className="px-3 py-2 text-xs text-ink-600 tabular-nums">
                            {new Date(inv.invoice_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm font-semibold text-ink-900 tabular-nums">
                            {balance.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input type="number" step="0.01" min={0} max={balance}
                              className="input w-28 text-right font-mono tabular-nums text-sm"
                              value={selectedInvs[inv.id] ?? ''}
                              disabled={!checked}
                              onChange={(e) => setSelectedInvs((p) => ({ ...p, [inv.id]: parseFloat(e.target.value) || 0 }))}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-canvas-subtle border-t border-ink-200">
                      <td colSpan={4} className="px-3 py-2 text-right text-xs font-semibold text-ink-700">Total receipt:</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-sage-700 tabular-nums">
                        {totalAmount.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
        </div>
      )}

      {/* Footer */}
      <div className="flex gap-2 justify-end pt-2 border-t border-ink-100">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Posting…' : `✓ Post ${voucherType} — ${totalAmount.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

// ── PaymentForm (CPV + BPV) ────────────────────────────────────────────────────
function PaymentForm({ voucherType, onClose, onSuccess }: {
  voucherType: 'CPV' | 'BPV'; onClose: () => void; onSuccess: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const methods = voucherType === 'CPV' ? CASH_METHODS : BANK_METHODS;

  const [date, setDate] = useState(today);
  const [reference, setReference] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierQ, setSupplierQ] = useState('');
  const [suppOpen, setSuppOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [method, setMethod] = useState<string>(voucherType === 'CPV' ? 'cash' : 'wire');
  const [bankAccountCode, setBankAccountCode] = useState('');
  const [gateway, setGateway] = useState('manual');
  const [gatewayTxnId, setGatewayTxnId] = useState('');
  const [selectedInvs, setSelectedInvs] = useState<Record<string, number>>({});

  const isOnline = ONLINE_METHODS.has(method);
  const totalAmount = Object.values(selectedInvs).reduce((s, v) => s + v, 0);

  const suppSearch = useQuery({
    queryKey: ['supp-search', supplierQ],
    queryFn: () => purchaseApi.listSuppliers({ q: supplierQ || undefined }),
    enabled: suppOpen || supplierQ.length >= 1,
  });
  const openInvs = useQuery({
    queryKey: ['open-vendor-invs', supplierId],
    queryFn: () => accountingApi.listVendorInvoices({ supplierId, status: 'open', limit: 50 }),
    enabled: !!supplierId,
  });
  const bankAccts = useQuery({ queryKey: ['bank-accts'], queryFn: () => accountingApi.listAccounts() });
  const bankAccountList = (bankAccts.data ?? []).filter((a: any) => a.is_bank_account);

  const mut = useMutation({
    mutationFn: () => accountingApi.recordVendorPayment({
      supplierId, paymentDate: date, method,
      amount: totalAmount,
      reference: reference || undefined,
      bankAccountCode: bankAccountCode || undefined,
      gateway: isOnline ? gateway : undefined,
      gatewayTxnId: isOnline && gatewayTxnId ? gatewayTxnId : undefined,
      voucherType,
      notes: description || undefined,
      applications: Object.entries(selectedInvs)
        .filter(([, amt]) => amt > 0)
        .map(([invoiceId, amount]) => ({ invoiceId, amount })),
    }),
    onSuccess: () => { toast.success(`${voucherType} posted successfully`); onSuccess(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = supplierId && totalAmount > 0;

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <VoucherNumber docType="vendor_payment" />
        <div>
          <label className="label">Voucher date</label>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Reference no</label>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)}
            placeholder="Cheque no / transfer ref" />
        </div>
      </div>

      <div>
        <label className="label">Supplier <span className="text-danger">*</span></label>
        {supplierId ? (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="flex-1 text-sm font-semibold text-amber-800">{supplierQ}</span>
            <button className="text-xs text-amber-600 hover:text-amber-800"
              onClick={() => { setSupplierId(''); setSupplierQ(''); setSelectedInvs({}); }}>Change ✕</button>
          </div>
        ) : (
          <div className="relative">
            <input className="input" placeholder="Search or pick a supplier…" value={supplierQ}
              onChange={(e) => setSupplierQ(e.target.value)}
              onFocus={() => setSuppOpen(true)}
              onBlur={() => setTimeout(() => setSuppOpen(false), 150)} />
            {suppOpen && (suppSearch.data?.length ?? 0) > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 card shadow-lg overflow-hidden">
                {suppSearch.data!.map((s: any) => (
                  <button key={s.id} className="w-full text-left px-4 py-2.5 hover:bg-canvas-subtle border-b border-ink-100 last:border-0 text-sm"
                    onClick={() => { setSupplierId(s.id); setSupplierQ(s.legal_name); setSuppOpen(false); }}>
                    <span className="font-semibold text-ink-900">{s.legal_name}</span>
                    <span className="text-ink-400 ml-2 font-mono text-xs">{s.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Payment method</label>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value)}>
            {methods.map((m) => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
          </select>
        </div>
        {voucherType === 'BPV' && (
          <div>
            <label className="label">Bank account</label>
            <select className="input" value={bankAccountCode} onChange={(e) => setBankAccountCode(e.target.value)}>
              <option value="">— Select bank account —</option>
              {bankAccountList.map((a: any) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isOnline && (
        <div className="p-4 bg-canvas-subtle rounded-xl border border-ink-200 space-y-3">
          <div className="eyebrow text-ink-500">Online transfer details</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Gateway / channel</label>
              <select className="input" value={gateway} onChange={(e) => setGateway(e.target.value)}>
                {GATEWAY_OPTIONS.map((g) => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Transaction ID</label>
              <input className="input font-mono text-xs" value={gatewayTxnId} onChange={(e) => setGatewayTxnId(e.target.value)}
                placeholder="TXN-XXXX" />
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="label">Description / narration</label>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder={`e.g. Payment to ${supplierQ || 'supplier'} against invoice`} />
      </div>

      {supplierId && (
        <div>
          <div className="eyebrow text-ink-500 mb-2">Apply to vendor invoices</div>
          {openInvs.isLoading ? <LoadingState rows={2} />
            : !openInvs.data?.length ? (
              <div className="p-4 text-center text-sm text-ink-400 bg-canvas-subtle rounded-xl border border-ink-200">
                No open vendor invoices for this supplier.
              </div>
            ) : (
              <div className="border border-ink-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-canvas-subtle border-b border-ink-200">
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Invoice</th>
                      <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Date</th>
                      <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Balance due</th>
                      <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Pay amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(openInvs.data ?? []).map((inv: any) => {
                      const balance = parseFloat(inv.balance_due);
                      const checked = inv.id in selectedInvs;
                      return (
                        <tr key={inv.id} className={clsx('border-b border-ink-100 last:border-0',
                          checked ? 'bg-amber-50/40' : 'hover:bg-canvas-subtle/50')}>
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedInvs((p) => ({ ...p, [inv.id]: balance }));
                                else setSelectedInvs((p) => { const n = { ...p }; delete n[inv.id]; return n; });
                              }} />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs font-bold text-ink-900">{inv.invoice_number}</td>
                          <td className="px-3 py-2 text-xs text-ink-600 tabular-nums">
                            {new Date(inv.invoice_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm font-semibold text-ink-900 tabular-nums">
                            {balance.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input type="number" step="0.01" min={0} max={balance}
                              className="input w-28 text-right font-mono tabular-nums text-sm"
                              value={selectedInvs[inv.id] ?? ''}
                              disabled={!checked}
                              onChange={(e) => setSelectedInvs((p) => ({ ...p, [inv.id]: parseFloat(e.target.value) || 0 }))}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-canvas-subtle border-t border-ink-200">
                      <td colSpan={4} className="px-3 py-2 text-right text-xs font-semibold text-ink-700">Total payment:</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-danger tabular-nums">
                        {totalAmount.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-2 border-t border-ink-100">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Posting…' : `✓ Post ${voucherType} — ${totalAmount.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

// ── JournalForm (JV) ───────────────────────────────────────────────────────────
type GLLine = { accountId: string; debit: string; credit: string; description: string };

function JournalForm({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<GLLine[]>([
    { accountId: '', debit: '0.00', credit: '0.00', description: '' },
    { accountId: '', debit: '0.00', credit: '0.00', description: '' },
  ]);

  const accountsQ = useQuery({ queryKey: ['accounts-all'], queryFn: () => accountingApi.listAccounts() });
  const accounts = accountsQ.data ?? [];

  const totals = useMemo(() => {
    const dr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const cr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { dr, cr, diff: Math.abs(dr - cr) };
  }, [lines]);

  const updateLine = (i: number, field: keyof GLLine, val: string) => {
    setLines((prev) => prev.map((l, j) => j === i ? { ...l, [field]: val } : l));
  };

  const mut = useMutation({
    mutationFn: () => accountingApi.createJournalEntry({
      entryDate: date,
      description,
      reference: reference || undefined,
      sourceType: 'manual',
      autoPost: true,
      // BUG-01: the API expects accountCode (the GL code string), not the account's
      // UUID. The select stores a.id, so the code has to be resolved here. Sending
      // accountId meant every line failed accountCode validation and no JV could ever
      // post, independently of the GL configuration.
      lines: lines
        .filter((l) => l.accountId && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0))
        .map((l, i) => ({
          accountCode: (accounts.find((a: any) => a.id === l.accountId) || {}).code,
          lineNo: i + 1,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description || undefined,
        })),
    }),
    onSuccess: () => { toast.success('Journal entry posted'); onSuccess(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = description && totals.diff < 0.005 && totals.dr > 0
    // BUG-01: require at least two complete lines, and refuse to submit while any line
    // carries an amount but no account. Such a line was silently dropped by the filter
    // above, which is what produced the confusing "Array must contain at least 2
    // element(s)" error instead of pointing at the incomplete row.
    && lines.filter((l) => l.accountId && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0)).length >= 2
    && !lines.some((l) => !l.accountId && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <VoucherNumber docType="je" />
        <div>
          <label className="label">Entry date</label>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Reference</label>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional ref" />
        </div>
        <div className="col-span-2">
          <label className="label">Narration / description <span className="text-danger">*</span></label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Accrual for Dec salaries, depreciation adjustment" />
        </div>
      </div>

      {/* GL Lines table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow text-ink-500">GL Lines (Double-Entry)</div>
          <button className="text-xs font-semibold text-ink-600 hover:text-ink-900 transition-colors px-2 py-1 rounded hover:bg-canvas-subtle"
            onClick={() => setLines((p) => [...p, { accountId: '', debit: '0.00', credit: '0.00', description: '' }])}>
            + Add Line
          </button>
        </div>

        <div className="border border-ink-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-canvas-subtle border-b border-ink-200">
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Account</th>
                <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500 w-28">Debit</th>
                <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500 w-28">Credit</th>
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Description</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-ink-100 last:border-0">
                  <td className="px-3 py-1.5">
                    <select className="input text-xs py-1"
                      value={l.accountId}
                      onChange={(e) => updateLine(i, 'accountId', e.target.value)}>
                      <option value="">— Select account —</option>
                      {accounts.map((a: any) => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <input type="number" step="0.01" min={0} className="input text-right font-mono text-sm tabular-nums py-1"
                      value={l.debit}
                      onChange={(e) => { updateLine(i, 'debit', e.target.value); if (parseFloat(e.target.value) > 0) updateLine(i, 'credit', '0.00'); }} />
                  </td>
                  <td className="px-3 py-1.5">
                    <input type="number" step="0.01" min={0} className="input text-right font-mono text-sm tabular-nums py-1"
                      value={l.credit}
                      onChange={(e) => { updateLine(i, 'credit', e.target.value); if (parseFloat(e.target.value) > 0) updateLine(i, 'debit', '0.00'); }} />
                  </td>
                  <td className="px-3 py-1.5">
                    <input className="input text-xs py-1" value={l.description}
                      onChange={(e) => updateLine(i, 'description', e.target.value)} placeholder="Description" />
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {lines.length > 2 && (
                      <button className="w-6 h-6 flex items-center justify-center text-ink-400 hover:text-danger rounded transition-colors"
                        onClick={() => setLines((p) => p.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-canvas-subtle border-t border-ink-200">
                <td className="px-3 py-2 text-xs font-semibold text-ink-600">Totals</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-ink-900 tabular-nums">{totals.dr.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-ink-900 tabular-nums">{totals.cr.toFixed(2)}</td>
                <td className="px-3 py-2" colSpan={2}>
                  {totals.diff < 0.005 && totals.dr > 0
                    ? <span className="text-xs text-sage-600 font-semibold">✓ Balanced</span>
                    : <span className="text-xs text-danger font-semibold">✗ Difference: {totals.diff.toFixed(2)}</span>
                  }
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="text-2xs text-ink-500 mt-1.5">
          Debit and credit must balance. Entry will be auto-posted to the general ledger.
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t border-ink-100">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Posting…' : `✓ Post Journal Entry — ${totals.dr.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}
